import * as React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  InputAdornment,
  Stack,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  TextField,
  Chip,
  Skeleton,
  Avatar,
  alpha,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';

// ─── Types ─────────────────────────────────────────────────────────────────────

type Order = 'asc' | 'desc';

export interface VendorRow {
  id: number;
  name: string;
  email: string;
  phoneNo: string;
  status: 'Active' | 'Inactive' | string;
  zipCode: string;
}

type SortableKey = keyof VendorRow;

interface HeadCell {
  id: SortableKey;
  label: string;
  numeric: boolean;
  minWidth?: number;
}

export interface VendorTableProps {
  /** Inject rows from parent/API; defaults to empty array. */
  initialRows?: VendorRow[];
  /** Called when user clicks "Add New Vendor". */
  onAdd?: () => void;
  /** Called when user clicks the edit icon. */
  onEdit?: (row: VendorRow) => void;
  /** Called when user confirms deletion; should return a Promise. */
  onDelete?: (id: number) => Promise<void>;
  /** Show skeleton loaders while true. */
  loading?: boolean;
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const HEAD_CELLS: readonly HeadCell[] = [
  { id: 'name', label: 'Company Name', numeric: false, minWidth: 180 },
  { id: 'phoneNo', label: 'Phone Number', numeric: false, minWidth: 140 },
  { id: 'email', label: 'Email', numeric: false, minWidth: 200 },
  { id: 'zipCode', label: 'Zip Code', numeric: false, minWidth: 100 },
  { id: 'status', label: 'Status', numeric: false, minWidth: 100 },
];

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

// ─── Utilities ─────────────────────────────────────────────────────────────────

function descendingComparator<T>(a: T, b: T, orderBy: keyof T): number {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (a: Record<Key, any>, b: Record<Key, any>) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

/** Returns initials from a name string, e.g. "Acme Corp" → "AC" */
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/** Deterministic hue from a string for avatar background colors */
function stringToHue(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return Math.abs(hash) % 360;
}

// ─── Sub-components ────────────────────────────────────────────────────────────

interface EnhancedTableHeadProps {
  order: Order;
  orderBy: SortableKey;
  onRequestSort: (event: React.MouseEvent<unknown>, property: SortableKey) => void;
}

const EnhancedTableHead: React.FC<EnhancedTableHeadProps> = ({ order, orderBy, onRequestSort }) => {
  const createSortHandler = (property: SortableKey) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {HEAD_CELLS.map((cell) => (
          <TableCell
            key={cell.id}
            sortDirection={orderBy === cell.id ? order : false}
            sx={{
              minWidth: cell.minWidth,
              fontWeight: 700,
              fontSize: '0.7rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'text.secondary',
              backgroundColor: (theme) => theme.palette.grey[50],
              borderBottom: (theme) => `2px solid ${theme.palette.divider}`,
              py: 1.5,
              whiteSpace: 'nowrap',
            }}
          >
            <TableSortLabel
              active={orderBy === cell.id}
              direction={orderBy === cell.id ? order : 'asc'}
              onClick={createSortHandler(cell.id)}
              sx={{
                '&.Mui-active': { color: 'primary.main' },
                '&:hover': { color: 'primary.main' },
              }}
            >
              {cell.label}
              {orderBy === cell.id && (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              )}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell
          align="center"
          sx={{
            fontWeight: 700,
            fontSize: '0.7rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'text.secondary',
            backgroundColor: (theme) => theme.palette.grey[50],
            borderBottom: (theme) => `2px solid ${theme.palette.divider}`,
            py: 1.5,
          }}
        >
          Actions
        </TableCell>
      </TableRow>
    </TableHead>
  );
};

const SkeletonRows: React.FC<{ count: number }> = ({ count }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <TableRow key={i}>
        <TableCell>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton variant="text" width={120} height={20} />
          </Stack>
        </TableCell>
        {['email', 'phoneNo', 'status'].map((k) => (
          <TableCell key={k}>
            <Skeleton variant="text" width="70%" height={20} />
          </TableCell>
        ))}
        <TableCell align="center">
          <Stack direction="row" spacing={1} justifyContent="center">
            <Skeleton variant="circular" width={28} height={28} />
            <Skeleton variant="circular" width={28} height={28} />
          </Stack>
        </TableCell>
      </TableRow>
    ))}
  </>
);

const EmptyState: React.FC<{ hasSearch: boolean }> = ({ hasSearch }) => (
  <TableRow>
    <TableCell colSpan={HEAD_CELLS.length + 1}>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={8}
        gap={1.5}
      >
        <StorefrontRoundedIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
        <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
          {hasSearch ? 'No results found' : 'No vendors yet'}
        </Typography>
        <Typography variant="body2" color="text.disabled" textAlign="center" maxWidth={300}>
          {hasSearch
            ? 'Try adjusting your search or clear the filter to see all vendors.'
            : 'Get started by adding your first vendor.'}
        </Typography>
      </Box>
    </TableCell>
  </TableRow>
);

interface StatusBadgeProps {
  status: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const isActive = status?.toLowerCase() === 'active';
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        fontSize: '0.72rem',
        fontWeight: 700,
        height: 22,
        borderRadius: 1,
        backgroundColor: isActive ? '#e8f5e9' : '#f5f5f5',
        color: isActive ? '#2e7d32' : '#757575',
        border: 'none',
      }}
    />
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

const VendorTable: React.FC<VendorTableProps> = ({
  initialRows = [],
  onAdd,
  onEdit,
  onDelete,
  loading = false,
}) => {
  const [rows, setRows] = React.useState<VendorRow[]>(initialRows);
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<SortableKey>('name');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [deleteTargetId, setDeleteTargetId] = React.useState<number | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Sync if parent updates initialRows
  React.useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  const handleRequestSort = React.useCallback(
    (_event: React.MouseEvent<unknown>, property: SortableKey) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [order, orderBy],
  );

  const filteredRows = React.useMemo(() => {
    const q = searchQuery.toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name?.toLowerCase().includes(q) ||
        r.email?.toLowerCase().includes(q) ||
        r.phoneNo?.toLowerCase().includes(q),
    );
  }, [rows, searchQuery]);

  const visibleRows = React.useMemo(
    () =>
      [...filteredRows]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, order, orderBy, page, rowsPerPage],
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const openDeleteDialog = (id: number) => setDeleteTargetId(id);
  const closeDeleteDialog = () => { if (!isDeleting) setDeleteTargetId(null); };

  const handleConfirmDelete = async () => {
    if (deleteTargetId === null) return;
    setIsDeleting(true);
    try {
      await onDelete?.(deleteTargetId);
      setRows((prev) => prev.filter((r) => r.id !== deleteTargetId));
      setSnackbar({ open: true, message: 'Vendor deleted successfully.', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete vendor. Please try again.', severity: 'error' });
    } finally {
      setIsDeleting(false);
      setDeleteTargetId(null);
    }
  };

  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        {/* ── Toolbar ── */}
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          px={3}
          py={2.5}
          gap={2}
          sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1}>
              Vendors
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {filteredRows.length} {filteredRows.length === 1 ? 'record' : 'records'}
              {searchQuery && ` matching "${searchQuery}"`}
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="stretch">
            <TextField
              size="small"
              placeholder="Search by name, email or phone…"
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: '100%', sm: 280 },
                '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.875rem' },
              }}
            />
            <Button
              onClick={onAdd}
              variant="contained"
              disableElevation
              startIcon={<AddRoundedIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
                whiteSpace: 'nowrap',
              }}
            >
              Add Vendor
            </Button>
          </Stack>
        </Box>

        {/* ── Table ── */}
        <TableContainer sx={{ maxHeight: 560 }}>
          <Table stickyHeader size="medium">
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {loading ? (
                <SkeletonRows count={rowsPerPage} />
              ) : visibleRows.length === 0 ? (
                <EmptyState hasSearch={!!searchQuery} />
              ) : (
                visibleRows.map((row) => {
                  const hue = stringToHue(row.name);
                  return (
                    <TableRow
                      hover
                      key={row.id}
                      sx={{
                        '&:last-child td': { border: 0 },
                        '&:hover': {
                          backgroundColor: (theme) =>
                            alpha(theme.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      {/* Name with avatar */}
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              backgroundColor: `hsl(${hue}, 55%, 88%)`,
                              color: `hsl(${hue}, 55%, 30%)`,
                            }}
                          >
                            {getInitials(row.name)}
                          </Avatar>
                          <Typography variant="body2" fontWeight={600}>
                            {row.name}
                          </Typography>
                        </Stack>
                      </TableCell>

                      {/* Email */}
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            maxWidth: 220,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.email}
                        </Typography>
                      </TableCell>

                      {/* Phone */}
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                          {row.phoneNo}
                        </Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <StatusBadge status={row.status} />
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Edit vendor" arrow>
                            <IconButton
                              size="small"
                              onClick={() => onEdit?.(row)}
                              aria-label={`Edit ${row.name}`}
                              sx={{
                                color: 'primary.main',
                                '&:hover': {
                                  backgroundColor: (theme) =>
                                    alpha(theme.palette.primary.main, 0.1),
                                },
                              }}
                            >
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete vendor" arrow>
                            <IconButton
                              size="small"
                              onClick={() => openDeleteDialog(row.id)}
                              aria-label={`Delete ${row.name}`}
                              sx={{
                                color: 'error.main',
                                '&:hover': {
                                  backgroundColor: (theme) =>
                                    alpha(theme.palette.error.main, 0.1),
                                },
                              }}
                            >
                              <DeleteRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Pagination ── */}
        <TablePagination
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}` }}
        />
      </Paper>

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog
        open={deleteTargetId !== null}
        onClose={closeDeleteDialog}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 3,
            maxWidth: 380,
            width: '100%',
            border: (theme) => `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 1 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <ErrorOutlineRoundedIcon sx={{ fontSize: 32, color: 'error.main' }} />
          </Box>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Delete Vendor?
          </Typography>
          <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
            This vendor will be permanently removed. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, pt: 2, gap: 1.5 }}>
          <Button
            onClick={closeDeleteDialog}
            disabled={isDeleting}
            variant="outlined"
            color="inherit"
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            variant="contained"
            color="error"
            disableElevation
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3 }}
          >
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Feedback Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2, fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default VendorTable;